import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/*
 * App-wide animated backdrop.
 *
 * Renders a single fixed, full-viewport layer BEHIND all page content
 * (z-index -1): a soft brand-coloured gradient base, drifting blobs and
 * shopping-themed emojis falling from the top. It also toggles the
 * `app-glass` class on <body>, which turns the app's cards glassy so the
 * moving backdrop shows through them (see the .app-glass rules in index.css).
 *
 * Skipped on the admin and delivery portals — those are data-heavy screens
 * (tables, charts) where translucent cards would hurt readability.
 */

/* The bag and diamond glyphs were dropped — they drifted across product
   photography and read as part of the product rather than the backdrop. */
const ICONS = [
  { char: '🛒', left: '5%', size: '2.3rem', delay: '0s', duration: '19s' },
  { char: '🎁', left: '23%', size: '2rem', delay: '8s', duration: '21s' },
  { char: '💳', left: '33%', size: '1.6rem', delay: '2s', duration: '26s' },
  { char: '📦', left: '42%', size: '2.2rem', delay: '10s', duration: '20s' },
  { char: '🏷️', left: '51%', size: '1.5rem', delay: '6s', duration: '25s' },
  { char: '⭐', left: '60%', size: '1.5rem', delay: '12s', duration: '22s' },
  { char: '👟', left: '69%', size: '1.9rem', delay: '3s', duration: '27s' },
  { char: '⌚', left: '87%', size: '1.6rem', delay: '7s', duration: '28s' },
  { char: '🎧', left: '95%', size: '1.8rem', delay: '16s', duration: '21s' },
  { char: '🛒', left: '64%', size: '1.5rem', delay: '20s', duration: '24s' },
  { char: '🎁', left: '18%', size: '1.6rem', delay: '15s', duration: '26s' }
];

function GlobalBackground() {
  const { pathname } = useLocation();
  const excluded =
    pathname.startsWith('/admin') || pathname.startsWith('/delivery');

  /* Glass theme is on for shop pages, off for admin/delivery portals */
  useEffect(() => {
    document.body.classList.toggle('app-glass', !excluded);

    return () => document.body.classList.remove('app-glass');
  }, [excluded]);

  if (excluded) {
    return null;
  }

  return (
    <div className="app-bg" aria-hidden="true">
      <span className="app-blob app-blob-1" />
      <span className="app-blob app-blob-2" />
      <span className="app-blob app-blob-3" />

      {ICONS.map((icon, i) => (
        <span
          key={i}
          className="app-float"
          style={{
            left: icon.left,
            fontSize: icon.size,
            animationDelay: icon.delay,
            animationDuration: icon.duration
          }}
        >
          {icon.char}
        </span>
      ))}

      <style>{globalBgStyles}</style>
    </div>
  );
}

const globalBgStyles = `
  .app-bg {
    position: fixed;
    inset: 0;
    z-index: -1;
    overflow: hidden;
    pointer-events: none;
    background:
      radial-gradient(760px 340px at 8% -8%, rgba(124,58,237,0.12), transparent 70%),
      radial-gradient(760px 340px at 92% 108%, rgba(234,88,12,0.14), transparent 70%),
      linear-gradient(180deg, #faf5ff 0%, #fff7ed 55%, #fef3c7 100%);
  }

  .app-blob {
    position: absolute;
    border-radius: 50%;
    filter: blur(70px);
    opacity: 0.5;
    will-change: transform;
  }
  .app-blob-1 {
    width: 360px; height: 360px;
    top: -100px; left: -90px;
    background: radial-gradient(circle at 30% 30%, rgba(234,88,12,0.4), transparent 70%);
    animation: appBlob1 20s ease-in-out infinite;
  }
  .app-blob-2 {
    width: 320px; height: 320px;
    bottom: -110px; right: -80px;
    background: radial-gradient(circle at 30% 30%, rgba(124,58,237,0.36), transparent 70%);
    animation: appBlob2 24s ease-in-out infinite;
  }
  .app-blob-3 {
    width: 280px; height: 280px;
    top: 42%; right: 14%;
    background: radial-gradient(circle at 30% 30%, rgba(245,158,11,0.32), transparent 70%);
    animation: appBlob3 28s ease-in-out infinite;
  }

  @keyframes appBlob1 {
    0%, 100% { transform: translate(0, 0) scale(1); }
    50% { transform: translate(60px, 44px) scale(1.15); }
  }
  @keyframes appBlob2 {
    0%, 100% { transform: translate(0, 0) scale(1); }
    50% { transform: translate(-52px, -42px) scale(1.1); }
  }
  @keyframes appBlob3 {
    0%, 100% { transform: translate(0, 0) scale(1); }
    50% { transform: translate(-44px, 52px) scale(1.2); }
  }

  /* Shopping emojis falling from the top */
  .app-float {
    position: absolute;
    top: -60px;
    line-height: 1;
    opacity: 0;
    filter: drop-shadow(0 6px 10px rgba(124,45,18,0.16));
    animation-name: appFall;
    animation-timing-function: linear;
    animation-iteration-count: infinite;
    will-change: transform, opacity;
  }

  @keyframes appFall {
    0% { transform: translateY(0) translateX(0) rotate(0deg); opacity: 0; }
    10% { opacity: 0.7; }
    50% { transform: translateY(55vh) translateX(22px) rotate(180deg); }
    90% { opacity: 0.7; }
    100% { transform: translateY(112vh) translateX(-14px) rotate(360deg); opacity: 0; }
  }

  @media (prefers-reduced-motion: reduce) {
    .app-blob, .app-float { animation: none !important; }
    .app-float { display: none; }
  }
`;

export default GlobalBackground;
